import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Button,
  IconButton,
  Fade,
  Backdrop,
  Stack,
  Menu,
  MenuItem,
} from "@mui/material";
import { Add, Close, Upload } from "@mui/icons-material";
import BasicBreadcrumbs from "../../components/breadcrumb";
import UploadGridUser from "./uploadGridUser";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import SuccessAlert from "../../helper/successAlert";
import ComponentBackdrop from "../../components/backdrop";
import { useApiClient } from "../../context/ApiClientContext";
import api from "../../helper/baseApi";
import ErrorAlert from "../../helper/errorAlert";
import { useNavigate } from "react-router-dom";

function UploadUserButton({ fullWidth, refreshData, flag, openBackdropOnboarding }) {
  const [open, setOpen] = useState(false);
  const [userData, setUserData] = useState([]);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openAnchorEl = Boolean(anchorEl);
  const [roleList, setRoleList] = useState([]);
  const [jobData, setJobData] = useState([]);
  const [jobGradeUploaded, setJobGradeUploaded] = useState(false);

  const { apiUrls: { mobileApiUrl } } = useApiClient();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOptionLists();
  }, []);

  const fetchOptionLists = async () => {
    try {
      setOpenBackdrop(true);
      // Fetch roles
      let response = await api.get(`${mobileApiUrl}user/user-optional`);
      setRoleList(response.data.data.roles || []);

      setOpenBackdrop(false);
    } catch (error) {
      ErrorAlert(error, navigate);
      setOpenBackdrop(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const callExportFn = () => {
    handleExport();
    handleCloseDropdown();
  };

  useEffect(() => {
    setJobGradeUploaded(false);
  }, [jobData]);

  const handleOnSubmit = async (e) => {
    e.preventDefault();

    // 🔍 Check for errors before submitting
    const hasErrors = userData.some(
      (row) => row.errors && Object.keys(row.errors).length > 0
    );

    if (hasErrors) {
      toast.error("Fix the invalid rows before submitting.");
      return;
    }

    try {
      if (flag === "user") {
        setOpenBackdrop(true);
      } else {
        openBackdropOnboarding(true);
      }

      // ✅ Step 1: upload job grades only if not already done
      const hasJobData =
        jobData && Array.isArray(jobData) && jobData.length > 0;

      // STEP 1: Upload job grades ONLY if there is data AND not already uploaded
      if (hasJobData && !jobGradeUploaded) {
        const responseJob = await api.post(
          `${mobileApiUrl}jobGrade/create-jobgrade-list`,
          { jobGradeList: jobData }
        );

        if (responseJob.status === 200) {
          console.log("✅ Job Grade upload success");
          setJobGradeUploaded(true); // mark as uploaded
        } else {
          // Job grade failed → stop, don’t call user API
          toast.error("Job Grade upload failed. Aborting user import.");
          setOpenBackdrop(false);
          return;
        }
      } else if (!hasJobData) {
        console.log("⚡ No job grade data → skipping job grade upload");
      } else {
        console.log("⚡ Job grade already uploaded → skipping upload");
      }

      // ✅ Step 2: prepare user payload
      const payload = {
        employees: userData
          .filter((user) => typeof user.no === "number")
          .map((user) => {
            const matchedRole = roleList.find((r) => r.roleName === user.role);
            const defaultRole = roleList.find((r) => r.roleName === "User");

            return {
              no: user.no,
              employeeCode: user.employeecode,
              fullName: user.fullname,
              role: matchedRole
                ? matchedRole.roleId
                : defaultRole.roleId || null,
              email: user.email,
              gender: user.gender,
              dob: user.dob,
              designation: user.designation,
              department: user.department,
              jobGrade: user.jobgrade,
            };
          }),
      };

      // ✅ Step 3: upload users
      const response = await api.post(`${mobileApiUrl}user/add-excel-user`, payload);

      if (response.status === 200) {
        if (flag === "user") {
          setTimeout(() => {
            handleClose();
            SuccessAlert(response.data.message);
            refreshData();
          }, 2000);
        } else {
          handleClose();
          SuccessAlert(response.data.message);
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      setTimeout(() => {
        ErrorAlert(error, navigate);
      }, 2000);
    } finally {
      if (flag === "user") {
        setTimeout(() => {
          setOpenBackdrop(false);
        }, 2000);
      } else {
        setTimeout(() => {
          openBackdropOnboarding(false);
        }, 2000);
      }
    }
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();

    const employeeSheet = workbook.addWorksheet("1. Employee Database");

    // --- TITLE ---
    employeeSheet.addRow(["Employee Database"]);
    employeeSheet.getCell("A1").font = { bold: true };
    employeeSheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left", wrapText: false };

    // --- INSTRUCTION ---
    employeeSheet.addRow(["Please fill up the following information."]);
    employeeSheet.getCell("A2").font = { italic: true, color: { argb: "FF6E6E6E" } }; // grey italic text
    employeeSheet.getCell("A2").alignment = { vertical: "top", horizontal: "left", wrapText: false };

    // --- SPACING ROW ---
    employeeSheet.addRow([]);

    // --- COLUMN WIDTH FOR "No." ---
    employeeSheet.getColumn(1).width = 15; // Fixed width for "No."

    // --- HEADER ROW ---
    const headerRow = employeeSheet.addRow([]); // create empty row first

    const headers = [
      { title: "No.", example: "1, 2, 3..." },
      { title: "Employee Code", example: "ABC001" },
      { title: "Full Name", example: "Ben Tan" },
      { title: "Role", example: "Mission Creator, User" },
      { title: "Email", example: "ben@abc.com" },
      { title: "Gender", example: "Male" },
      { title: "DOB", example: "28.08.1988" },
      { title: "Designation", example: "Business Director" },
      { title: "Department", example: "Human Resource" },
      { title: "Job Grade", example: "G5" },
    ];

    // --- APPLY HEADER STYLE ---
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = {
        richText: [
          { text: `${h.title}\n`, font: { bold: true, color: { argb: "FFFFFFFF" } } },
          { text: `(e.g. ${h.example})`, font: { italic: true, color: { argb: "FFDDDDDD" } } },
        ],
      };
      cell.alignment = { vertical: "top", horizontal: "left", wrapText: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF39493F" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // --- ROLE DROPDOWN (data validation for the Role column) ---
    // Role column index (4th column)
    const roleColIndex = 4;
    const roleOptions = ["User", "Mission Creator", "POISUM Admin"];
    const roleFormula = `"${roleOptions.join(",")}"`;

    // Apply data validation for a reasonable number of rows below the header
    const firstDataRow = headerRow.number + 1;
    const lastDataRow = firstDataRow + 999; // e.g. 1000 rows of validation
    for (let r = firstDataRow; r <= lastDataRow; r++) {
      const cell = employeeSheet.getCell(r, roleColIndex);
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [roleFormula],
        showInputMessage: true,
        promptTitle: "Select Role",
        prompt: `Choose one of: ${roleOptions.join(", ")}`,
        showErrorMessage: true,
        errorTitle: "Invalid Role",
        error: "Please select a role from the list.",
      };
      // keep alignment consistent
      cell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
    }

    // --- AUTO WIDTH FOR OTHER COLUMNS (EXCEPT COLUMN 1) ---
    employeeSheet.columns.forEach((col, idx) => {
      if (idx === 0) return; // Skip column "No."

      let maxLength = 0;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value
          ? (typeof cell.value === "object" && cell.value.richText
            ? cell.value.richText.map((t) => t.text).join("\n") // preserve the newline between title and example
            : cell.value.toString())
          : "";
        maxLength = Math.max(maxLength, cellValue.length);

        // Enable wrap text so the "(e.g. ...)" appears on a new line
        cell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
      });

      // Reasonable width with caps
      col.width = Math.min(Math.max(maxLength + 5, 15), 60);
    });

    // --- Job Grade Sheet ---
    const jobGradeSheet = workbook.addWorksheet("2. Job Grade");

    // --- TITLE & DESCRIPTION ---
    jobGradeSheet.addRow(["Job Grade"]);
    jobGradeSheet.getCell("A1").font = { bold: true };
    jobGradeSheet.getCell("A1").alignment = { vertical: "middle", horizontal: "left", wrapText: false };

    jobGradeSheet.addRow([
      "A classification of employee level or position within the company’s hierarchy, used in POISUM to group roles by seniority and responsibility.",
    ]);
    jobGradeSheet.getCell("A2").alignment = { vertical: "top", horizontal: "left", wrapText: false };

    jobGradeSheet.addRow([]);

    // --- INSTRUCTION ---
    jobGradeSheet.addRow(["Instruction:"]);
    jobGradeSheet.getCell("A4").font = { bold: true };
    jobGradeSheet.getCell("A4").alignment = { vertical: "middle", horizontal: "left", wrapText: false };

    jobGradeSheet.addRow(["Please list down all the job grade in your company."]);
    jobGradeSheet.getCell("A5").alignment = { vertical: "top", horizontal: "left", wrapText: false };

    jobGradeSheet.addRow([]);

    // --- MAIN TABLE HEADER (LEFT SIDE) ---
    const mainHeaderRow = jobGradeSheet.addRow(["No.", "Job Grade", "Description"]);
    mainHeaderRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF39493F" },
      };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // --- EXAMPLE TITLE ---
    jobGradeSheet.getCell("E6").value = "Example:";
    jobGradeSheet.getCell("E6").font = { italic: true, color: { argb: "FF6E6E6E" } };
    jobGradeSheet.getCell("E6").alignment = { vertical: "middle", horizontal: "left", wrapText: false };

    // --- EXAMPLE TABLE HEADER (RIGHT SIDE) ---
    const exampleHeaderRow = jobGradeSheet.getRow(7);
    ["No.", "Job Grade", "Description"].forEach((title, i) => {
      const cell = exampleHeaderRow.getCell(5 + i);
      cell.value = title;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF39493F" },
      };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // --- EXAMPLE DATA ROWS ---
    const exampleData = [
      ["1", "G1", "Intern / Part Timer"],
      ["2", "G2", "Executive / Engineer"],
      ["3", "G3", "Senior Executive / Engineer"],
      ["4", "G4", "Asst Manager"],
      ["5", "G5", "Manager / Specialist"],
      ["6", "G6", "Director / C-levels"],
    ];

    exampleData.forEach((dataRow, i) => {
      const row = jobGradeSheet.getRow(8 + i);
      row.getCell(5).value = dataRow[0];
      row.getCell(6).value = dataRow[1];
      row.getCell(7).value = dataRow[2];
      [5, 6, 7].forEach((col) => {
        const cell = row.getCell(col);
        cell.alignment = { vertical: "top", horizontal: "left", wrapText: false };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // --- COLUMN WIDTHS ---
    // Left table
    jobGradeSheet.getColumn(1).width = 5;  // No.
    jobGradeSheet.getColumn(2).width = 15; // Job Grade
    jobGradeSheet.getColumn(3).width = 30; // Description

    // Space between tables (D)
    jobGradeSheet.getColumn(4).width = 15;

    // Right table
    jobGradeSheet.getColumn(5).width = 5;
    jobGradeSheet.getColumn(6).width = 15;
    jobGradeSheet.getColumn(7).width = 30;



    // --- Role Sheet ---
    const roleSheet = workbook.addWorksheet("3. Role");

    // ===== TITLE =====
    roleSheet.addRow(["Glossary : Role"]);
    const titleCell = roleSheet.getCell("A1");
    titleCell.font = { bold: true, size: 12 };
    titleCell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };

    // ===== DESCRIPTION =====
    const descRow = roleSheet.addRow([
      "Everyone plays a part — some create missions, some run the portal, and everyone joins in to earn rewards.",
    ]);
    const descRowIndex = descRow.number;
    roleSheet.mergeCells(descRowIndex, 1, descRowIndex, 4);
    const descCell = roleSheet.getCell(descRowIndex, 1);
    descCell.font = { size: 11 };
    descCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };

    roleSheet.addRow([]);

    // ===== INSTRUCTION =====
    const instructionTitleRow = roleSheet.addRow(["Instruction:"]);
    instructionTitleRow.getCell(1).font = { bold: true };
    instructionTitleRow.getCell(1).alignment = { vertical: "middle", horizontal: "left", wrapText: false };

    const instructionRow = roleSheet.addRow([
      "All employees are set as User by default. Only update the Role column to POISUM Admin or Mission Creator where applicable in 'Employee Database' sheet.",
    ]);
    const instructionRowIndex = instructionRow.number;
    roleSheet.mergeCells(instructionRowIndex, 1, instructionRowIndex, 4);
    const instructionCell = roleSheet.getCell(instructionRowIndex, 1);
    instructionCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };

    roleSheet.addRow([]);

    // ===== HEADER =====
    const headersRole = ["No.", "Role", "Description"];
    const headerRowRole = roleSheet.addRow(headersRole);

    headerRowRole.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF39493F" }, // dark green/grey background
      };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // white text
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // ===== DATA ROWS =====
    const roleData = [
      [
        "1",
        "POISUM Admin",
        "A designated person with access to the POISUM Portal (backend), which serves as the company’s database and performance dashboard. Typically assigned to HR personnel and top management (decision-makers), this role oversees system setup, monitors overall performance, and ensures data accuracy.\nPOISUM Administrators automatically have Mission Creator privileges within the POISUM app.",
      ],
      [
        "2",
        "Mission Creator",
        "An appointed individual with the authority to create and manage missions, events, and campaigns within the POISUM app. They can track participation and access performance analytics for the activities they initiate, ensuring alignment with company objectives.",
      ],
      [
        "3",
        "User",
        "All company employees who have access to download and use the POISUM app. Users can participate in missions, events, and campaigns, accumulate points, and redeem rewards as defined by the company’s POISUM setup.",
      ],
    ];

    roleData.forEach((row) => {
      const dataRow = roleSheet.addRow(row);
      dataRow.eachCell((cell, colNumber) => {
        cell.alignment = {
          vertical: "top",
          horizontal: colNumber === 3 ? "left" : "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // ===== COLUMN WIDTHS =====
    roleSheet.getColumn(1).width = 10; // Fixed width for "No."
    roleSheet.getColumn(2).width = 25; // Role name
    roleSheet.getColumn(3).width = 100; // Description (wide enough for long text)

    // ===== GENERAL ALIGNMENT CLEANUP =====
    roleSheet.columns.forEach((col) => {
      col.eachCell({ includeEmpty: true }, (cell) => {
        if (!cell.alignment) {
          cell.alignment = { vertical: "top", horizontal: "left", wrapText: true };
        }
      });
    });

    // set all font to Inter size 11
    workbook.eachSheet((sheet) => {
      sheet.eachRow((row) => {
        row.eachCell((cell) => {
          // preserve existing font styles (bold, italic, color, etc.)
          cell.font = { ...cell.font, name: "Inter", size: 11 };
        });
      });
    });

    // Export Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "Poisum_Employee_Database.xlsx");
  };

  return (
    <Box
      my={{ xs: 1, md: 2 }}
      sx={{
        display: "flex",
        alignItems: "end",
      }}
    >
      <div>
        {flag == 'user' ? (<Button
          id="basic-button"
          aria-controls={openAnchorEl ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openAnchorEl ? 'true' : undefined}
          onClick={handleClick}
          size="large"
          startIcon={<Add />}
          sx={{
            backgroundColor: "secondary.main",
            fontWeight: "600",
            fontSize: 14,
            color: "#fff",
            boxShadow: 1, // subtle default shadow
            "&:hover": {
              backgroundColor: "secondary.dark",
              boxShadow: 2, // slightly stronger on hover
            },
          }}
        >
          Upload user
        </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            component="label"
            startIcon={<Upload />}
            onClick={handleOpen}
            sx={{
              px: 6,
              py: 1.5,
              textTransform: "none",
              fontSize: "14px",
            }}
          >
            Upload File
          </Button>
        )}

        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={openAnchorEl}
          onClose={handleCloseDropdown}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              mt: 1, // small spacing below the button
              minWidth: 180, // optional: set a minimum width
              borderRadius: 1, // rounded corners
              boxShadow: 3, // subtle shadow
              backgroundColor: 'secondary.main',
              color: "white"
            },
          }}
        >
          <MenuItem sx={{ fontSize: "14px" }} onClick={handleOpen}>Upload file</MenuItem>
          <MenuItem sx={{ fontSize: "14px" }} onClick={callExportFn}>Get upload template</MenuItem>
        </Menu>

      </div>

      <Modal
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose(event, reason);
          }
        }}
        closeAfterTransition
        // slots={{ backdrop: Backdrop }}
        // slotProps={{ backdrop: { timeout: 500 } }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={open} timeout={500}>
          <Box
            sx={{
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              overflowY: "auto",
              position: "relative",
              width: { xs: "90%" },
              maxHeight: "90vh",
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 6,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 2,
                my: 3,
              }}
            >
              <BasicBreadcrumbs first="User Management" />

              {userData.length > 0 && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    alignItems: { xs: "stretch", sm: "flex-start" },
                  }}
                >
                  <Button
                    variant="contained"
                    size="medium"
                    fullWidth={fullWidth}
                    onClick={handleOnSubmit}
                    startIcon={<Add />}
                    sx={{
                      backgroundColor: "secondary.main",
                      fontWeight: "600",
                      "&:hover": {
                        backgroundColor: "secondary.dark",
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Stack>
              )}
            </Box>

            <UploadGridUser onDataChange={setUserData} onJobData={setJobData} />

          </Box>
        </Fade>
      </Modal>
      {flag === 'user' && <ComponentBackdrop openBackdrop={openBackdrop} />}
    </Box>
  );
}

export default UploadUserButton;